import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  Loader2,
  RefreshCw,
  Trash2,
  RotateCcw,
  Server,
  Cpu,
  HardDrive,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  Power,
  Settings,
} from 'lucide-react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  fetchSharedServer,
  fetchSharedServerBuilds,
  rebuildSharedServerBuild,
  destroySharedServerBuild,
  cancelSharedServerBuild,
  retrySharedServerBuild,
  initializeSharedServer,
  toggleSharedServer,
  testSharedServerConnection,
  configureSharedServer,
} from '@/api/admin';
import { toast } from 'sonner';

export function SharedServerDetailPage() {
  const { serverId } = useParams<{ serverId: string }>();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [capacity, setCapacity] = useState({
    max_active_builds: 10,
    max_concurrent_initializing: 1,
    priority_weight: 100,
  });

  const { data: serverData, isLoading: serverLoading, error: serverError, refetch, isFetching } = useQuery({
    queryKey: ['admin-shared-server', serverId],
    queryFn: () => fetchSharedServer(Number(serverId)),
    enabled: !!serverId,
    refetchInterval: autoRefresh ? 10000 : false,
  });

  const { data: buildsData, isLoading: buildsLoading } = useQuery({
    queryKey: ['admin-shared-server-builds', serverId, page],
    queryFn: () => fetchSharedServerBuilds(Number(serverId), page),
    enabled: !!serverId,
    refetchInterval: autoRefresh ? 10000 : false,
  });

  // Initialize capacity from server data
  useEffect(() => {
    if (serverData) {
      setCapacity({
        max_active_builds: serverData.max_active_builds || 10,
        max_concurrent_initializing: serverData.max_concurrent_initializing || 1,
        priority_weight: serverData.priority_weight || 100,
      });
    }
  }, [serverData]);

  const toggleMutation = useMutation({
    mutationFn: () => toggleSharedServer(Number(serverId)),
    onSuccess: () => {
      toast.success('Availability toggled');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-server', serverId] });
      queryClient.invalidateQueries({ queryKey: ['admin-shared-servers'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to toggle'),
  });

  const initMutation = useMutation({
    mutationFn: () => initializeSharedServer(Number(serverId)),
    onSuccess: () => toast.success('Initialization started'),
    onError: (err: any) => toast.error(err.message || 'Failed to initialize'),
  });

  const testMutation = useMutation({
    mutationFn: () => testSharedServerConnection(Number(serverId)),
    onSuccess: () => toast.success('Connection OK'),
    onError: (err: any) => toast.error(err.message || 'Connection failed'),
  });

  const capacityMutation = useMutation({
    mutationFn: () => configureSharedServer(Number(serverId), capacity),
    onSuccess: () => {
      toast.success('Capacity updated');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-server', serverId] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update capacity'),
  });

  const rebuildMutation = useMutation({
    mutationFn: (buildId: number) => rebuildSharedServerBuild(buildId),
    onSuccess: () => {
      toast.success('Rebuild queued');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-server-builds', serverId] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to rebuild'),
  });

  const destroyMutation = useMutation({
    mutationFn: (buildId: number) => destroySharedServerBuild(buildId),
    onSuccess: () => {
      toast.success('Destroy queued');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-server-builds', serverId] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to destroy'),
  });

  const cancelMutation = useMutation({
    mutationFn: (buildId: number) => cancelSharedServerBuild(buildId),
    onSuccess: () => {
      toast.success('Cancelled');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-server-builds', serverId] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to cancel'),
  });

  const retryMutation = useMutation({
    mutationFn: (buildId: number) => retrySharedServerBuild(buildId),
    onSuccess: () => {
      toast.success('Retry queued');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-server-builds', serverId] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to retry'),
  });

  const handleToggle = () => {
    const action = serverData?.available ? 'disable' : 'enable';
    if (confirm(`Are you sure you want to ${action} this server?`)) {
      toggleMutation.mutate();
    }
  };

  const handleInit = () => {
    if (confirm('Initialize server? This will run the initialization playbook.')) {
      initMutation.mutate();
    }
  };

  const handleDestroy = (buildId: number, fqdn?: string) => {
    if (confirm(`Destroy build "${fqdn || `#${buildId}`}"? This action cannot be undone.`)) {
      destroyMutation.mutate(buildId);
    }
  };

  const getStatusBadge = (status: string) => {
    const lower = status?.toLowerCase() || '';
    if (lower === 'ready' || lower === 'active' || lower === 'running') {
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          <CheckCircle className="h-3 w-3 mr-1" />
          {status}
        </Badge>
      );
    }
    if (lower.includes('error') || lower === 'failed') {
      return (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" />
          {status}
        </Badge>
      );
    }
    if (lower === 'pending' || lower === 'queued' || lower === 'initializing') {
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          <Clock className="h-3 w-3 mr-1" />
          {status}
        </Badge>
      );
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  if (serverLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  if (serverError || !serverData) {
    return (
      <AdminLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load server.</AlertDescription>
        </Alert>
      </AdminLayout>
    );
  }

  const builds = buildsData?.builds || [];
  const pagination = buildsData?.pagination;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/shared-servers">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold">{serverData.name || serverData.ip_address}</h1>
            <p className="text-sm text-muted-foreground">
              {serverData.ip_address}:{serverData.port}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh</Label>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Server className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <div className="mt-1">{getStatusBadge(serverData.status)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Power className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Availability</p>
                <p className="text-lg font-semibold">
                  {serverData.available ? 'Available' : 'Unavailable'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Cpu className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Builds</p>
                <p className="text-2xl font-semibold">{builds.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Settings className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Max Builds</p>
                <p className="text-2xl font-semibold">{capacity.max_active_builds}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Server Actions */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Server Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
            >
              <Activity className="h-4 w-4 mr-2" />
              Test Connection
            </Button>
            <Button
              variant="outline"
              onClick={handleInit}
              disabled={initMutation.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Initialize
            </Button>
            <Button
              variant="outline"
              onClick={handleToggle}
              disabled={toggleMutation.isPending}
            >
              <Power className="h-4 w-4 mr-2" />
              {serverData.available ? 'Disable' : 'Enable'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Capacity Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Settings className="h-4 w-4" />
              Capacity Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="max_active_builds">Max Active Builds</Label>
              <Input
                id="max_active_builds"
                type="number"
                value={capacity.max_active_builds}
                onChange={(e) =>
                  setCapacity((c) => ({ ...c, max_active_builds: Number(e.target.value) }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_concurrent_initializing">Max Concurrent Initializing</Label>
              <Input
                id="max_concurrent_initializing"
                type="number"
                value={capacity.max_concurrent_initializing}
                onChange={(e) =>
                  setCapacity((c) => ({ ...c, max_concurrent_initializing: Number(e.target.value) }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority_weight">Priority Weight</Label>
              <Input
                id="priority_weight"
                type="number"
                value={capacity.priority_weight}
                onChange={(e) =>
                  setCapacity((c) => ({ ...c, priority_weight: Number(e.target.value) }))
                }
              />
            </div>
            <Button
              className="w-full"
              onClick={() => capacityMutation.mutate()}
              disabled={capacityMutation.isPending}
            >
              {capacityMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Save Settings
            </Button>
          </CardContent>
        </Card>

        {/* Load Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <HardDrive className="h-4 w-4" />
              System Load
            </CardTitle>
          </CardHeader>
          <CardContent>
            {serverData.load_info && Object.keys(serverData.load_info).length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(serverData.load_info).map(([key, value]) => (
                  <div key={key} className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-lg font-semibold">{String(value)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <HardDrive className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No load information available</p>
                <Button variant="outline" className="mt-4" onClick={() => testMutation.mutate()}>
                  Test connection to fetch load info
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Builds Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cpu className="h-5 w-5" />
            Builds ({pagination?.total || builds.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {buildsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : builds.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b">
                      <th className="py-3 px-2 font-medium">ID</th>
                      <th className="py-3 px-2 font-medium">Domain</th>
                      <th className="py-3 px-2 font-medium">Status</th>
                      <th className="py-3 px-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {builds.map((build) => (
                      <tr key={build.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-2">
                          <code className="text-xs">#{build.id}</code>
                        </td>
                        <td className="py-3 px-2">
                          <span className="font-medium">{build.fqdn || '—'}</span>
                        </td>
                        <td className="py-3 px-2">
                          {getStatusBadge(build.status)}
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex flex-wrap gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => rebuildMutation.mutate(build.id)}
                              disabled={rebuildMutation.isPending}
                              title="Rebuild"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => retryMutation.mutate(build.id)}
                              disabled={retryMutation.isPending}
                              title="Retry"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => cancelMutation.mutate(build.id)}
                              disabled={cancelMutation.isPending}
                              title="Cancel"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDestroy(build.id, build.fqdn)}
                              disabled={destroyMutation.isPending}
                              title="Destroy"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 text-sm">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-muted-foreground">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Cpu className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No builds on this server</p>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
